window.theme = window.theme || {};
theme.predictiveSearch = theme.predictiveSearch || {};
theme.predictiveSearch.cache = theme.predictiveSearch.cache || Object.create(null);
theme.predictiveSearch.instanceCount = theme.predictiveSearch.instanceCount || 0;

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.state = theme.predictiveSearch;
    this.instanceId = ++this.state.instanceCount;
    this.resultsId = `predictive-search-results-list-${this.instanceId}`;
    this.isOpen = false;
    this.searchTerm = '';
    this.requestController = null;
    this.requestSequence = 0;

    if (!this.input || !this.predictiveSearchResults) return;

    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.onInput = debounce(this.onChange.bind(this), 200);
    this.onFormSubmitHandler = this.onFormSubmit.bind(this);
    this.onCloseButtonClick = this.close.bind(this);
    this.onClearButtonClick = this.clear.bind(this);
    this.onFocusHandler = this.onFocus.bind(this);
    this.onFocusOutHandler = this.onFocusOut.bind(this);
    this.onKeyupHandler = this.onKeyup.bind(this);
    this.onKeydownHandler = this.onKeydown.bind(this);

    this.querySelector('form.search')?.addEventListener('submit', this.onFormSubmitHandler);
    this.querySelector('button[type="button"]')?.addEventListener('click', this.onCloseButtonClick);
    this.querySelector('button[type="reset"]')?.addEventListener('click', this.onClearButtonClick);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocusHandler);
    this.addEventListener('focusout', this.onFocusOutHandler);
    this.addEventListener('keyup', this.onKeyupHandler);
    this.addEventListener('keydown', this.onKeydownHandler);
  }

  normalizeQuery(value) {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    const previousTerm = this.searchTerm;

    if (!newSearchTerm.length) {
      this.searchTerm = '';
      this.clear();
      this.close(true);
      return;
    }

    if (!previousTerm || !newSearchTerm.toLowerCase().startsWith(previousTerm.toLowerCase())) {
      this.querySelector('[data-predictive-search-groups]')?.remove();
    }

    this.updateSearchForTerm(previousTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    const selectedLink = this.querySelector('[aria-selected="true"] a');

    if (!query.length) {
      event.preventDefault();
      return;
    }

    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(currentSearchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText && previousTerm) {
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li[role="option"], button[role="option"]');
    let activeElement = allElements[0];

    if (!activeElement || (moveUp && !selectedElement)) return;

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  cancelRequest() {
    if (this.requestController) this.requestController.abort();
    this.requestController = null;
    this.requestSequence += 1;
  }

  getSearchResults(searchTerm) {
    const queryKey = this.normalizeQuery(searchTerm);
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();
    this.cancelRequest();
    const requestSequence = this.requestSequence;

    if (this.state.cache[queryKey]) {
      this.renderSearchResults(this.state.cache[queryKey]);
      return;
    }

    const controller = new AbortController();
    this.requestController = controller;
    const params = new URLSearchParams({
      q: searchTerm,
      'resources[limit]': perPage,
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search',
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (requestSequence !== this.requestSequence || this.normalizeQuery(this.getQuery()) !== queryKey) return;

        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section was not returned');

        this.state.cache[queryKey] = section.innerHTML;
        this.renderSearchResults(section.innerHTML);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        if (requestSequence === this.requestSequence) {
          this.removeAttribute('loading');
          this.setLiveRegionText('');
          console.error(error);
        }
      })
      .finally(() => {
        if (this.requestController === controller) this.requestController = null;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.loadingText) this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    window.setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  prepareResultsMarkup(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    const resultRoot = template.content.querySelector('[data-predictive-search-results]');
    if (!resultRoot) return resultsMarkup;

    const idMap = new Map();
    const originalRootId = resultRoot.id;
    const elementsWithIds = [resultRoot, ...resultRoot.querySelectorAll('[id]')];
    elementsWithIds.forEach((element) => {
      if (!element.id) return;
      const originalId = element.id;
      const scopedId = `${originalId}-${this.instanceId}`;
      idMap.set(originalId, scopedId);
      element.id = scopedId;
    });

    if (originalRootId) idMap.set(originalRootId, this.resultsId);
    resultRoot.id = this.resultsId;

    resultRoot.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns]').forEach((element) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;
        const scopedValue = value.split(/\s+/).map((id) => idMap.get(id) || id).join(' ');
        element.setAttribute(attribute, scopedValue);
      });
    });

    return template.innerHTML;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = this.prepareResultsMarkup(resultsMarkup);
    this.setAttribute('results', 'true');
    this.modal?.classList.add('searching');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) this.setLiveRegionText(countElement.textContent.trim());
  }

  getResultsMaxHeight() {
    const header = this.modal?.closest('.shopify-section-header')?.querySelector('.header') || document.querySelector('.header');
    const headerBottom = header?.getBoundingClientRect().bottom || 0;
    return Math.max(200, window.innerHeight - headerBottom);
  }

  open() {
    this.modal?.classList.add('searching');
    const isMobileModal = this.modal && window.matchMedia('(max-width: 749px)').matches;
    if (isMobileModal) {
      this.predictiveSearchResults.style.removeProperty('max-height');
    } else {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.cancelRequest();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event?.preventDefault();
    this.cancelRequest();
    this.input.value = '';
    this.searchTerm = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    document.body.classList.remove('predictive-search--focus');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
